import { useMemo, useRef } from 'react';
import { Destination } from '@shared/models/destination';
import styles from './DepartureOrArrivalTableDescriptionCell.module.scss';
import ImageIcon from '@shared/components/ImageIcon';
import { DepartureOrArrivalTimeFormat } from '@shared/models/departureOrArrivalTimeFormat';
import { TrafficSituationSeverity } from '@shared/models/trafficSituationSeverity';
import { Line as LineModel } from '@shared/models/line';
import { TransportMode } from '@shared/models/transportMode';
import { longestWordWidth } from '@shared/utils/longestWordWidth';
import useElementSize from '@shared/hooks/useElementSize';
import Line from './Line';
import classNames from 'classnames';

const DESTINATION_FONT = 'H3';
const VIA_FONT = 'H4R';

interface DepartureOrArrivalTableDestinationCellProps {
  line: LineModel;
  transportMode: TransportMode;
  destinationOrOrigin: Readonly<Destination> | string;
  tags?: readonly string[];
  timeFormat: DepartureOrArrivalTimeFormat;
  nextTrafficSituationSeverity?: TrafficSituationSeverity;
  isNextWheelchairAccessible: boolean;
  headerWidth?: number;
}

export default function DepartureOrArrivalTableDestinationCell({
  line,
  transportMode,
  destinationOrOrigin,
  tags,
  timeFormat,
  nextTrafficSituationSeverity,
  isNextWheelchairAccessible,
  headerWidth,
}: DepartureOrArrivalTableDestinationCellProps) {
  const textsRef = useRef<HTMLDivElement>(null);
  const availableWidth = useElementSize(textsRef).width;

  function getTransportModeText(transportMode: TransportMode): string {
    switch (transportMode) {
      case 'tram':
        return 'Spårvagn';

      case 'ferry':
        return 'Båt';

      case 'train':
        return 'Tåg';

      case 'bus':
        return 'Buss';
    }
  }

  const destinationOrOriginText = useMemo(() => {
    if (typeof destinationOrOrigin === 'string') {
      return destinationOrOrigin;
    }

    const fullNameFits =
      availableWidth === undefined ||
      longestWordWidth(destinationOrOrigin.name, DESTINATION_FONT) <=
        availableWidth;
    if (fullNameFits) {
      return destinationOrOrigin.name;
    }

    return destinationOrOrigin.shortName ?? destinationOrOrigin.name;
  }, [destinationOrOrigin, availableWidth]);

  const destinationOrOriginNeedEllipsis = useMemo(() => {
    if (availableWidth === undefined) {
      return false;
    }
    return (
      longestWordWidth(destinationOrOriginText, DESTINATION_FONT) >
      availableWidth
    );
  }, [destinationOrOriginText, availableWidth]);

  const viaText = useMemo(() => {
    if (typeof destinationOrOrigin === 'string') {
      return undefined;
    }

    const fullViaFits =
      availableWidth === undefined ||
      destinationOrOrigin.via === undefined ||
      longestWordWidth(destinationOrOrigin.via, VIA_FONT) <= availableWidth;
    if (fullViaFits) {
      return destinationOrOrigin.via;
    }

    return destinationOrOrigin.shortVia ?? destinationOrOrigin.via;
  }, [destinationOrOrigin, availableWidth]);

  const viaNeedEllipsis = useMemo(() => {
    if (availableWidth === undefined || viaText === undefined) {
      return false;
    }
    return longestWordWidth(viaText, VIA_FONT) > availableWidth;
  }, [viaText, availableWidth]);

  return (
    <td
      className={styles.description}
      // Pin to the measured header width when provided (visible table); otherwise
      // take the leftover space while measuring (measurement table).
      style={
        headerWidth !== undefined
          ? { width: headerWidth, maxWidth: headerWidth }
          : { width: '100%', maxWidth: 0 }
      }
    >
      <div className={styles.container}>
        <div className={styles['line-wrapper']}>
          <Line line={line} size="medium" />
        </div>
        <div ref={textsRef} className={styles.texts}>
          <div
            className={classNames(
              styles['destination-or-origin'],
              destinationOrOriginNeedEllipsis &&
                styles['destination-or-origin--overflown'],
            )}
          >
            {destinationOrOriginText}
          </div>

          {viaText && (
            <div
              className={classNames(
                styles['via'],
                viaNeedEllipsis && styles['via--overflown'],
              )}
            >
              via {viaText}
            </div>
          )}
          <div className={styles['tag']}>
            <ImageIcon
              type={`transport-mode-${transportMode}`}
              alt={getTransportModeText(transportMode)}
              className={styles['transport-mode']}
            ></ImageIcon>
            <span aria-hidden="true">
              {getTransportModeText(transportMode)}
            </span>
          </div>
          {tags?.map((tag, tagIndex) => (
            <span key={tag} className={styles['tag']}>
              {tag}
              {tagIndex !== tags.length - 1 && (
                <span className="sr-only">, </span>
              )}
            </span>
          ))}
          {timeFormat === 'time' && isNextWheelchairAccessible && (
            <ImageIcon
              type="wheelchair-accessible"
              alt="Resan är anpassad för rullstol"
              className={styles.icon}
            ></ImageIcon>
          )}
          {timeFormat === 'time' && nextTrafficSituationSeverity && (
            <ImageIcon
              type={`traffic-situation-${nextTrafficSituationSeverity}`}
              alt="Avgången har ett störningsmeddelande"
              className={styles.icon}
            ></ImageIcon>
          )}
        </div>
      </div>
    </td>
  );
}
